import type {
  ThemeBottomSectionImageItem,
  ThemeBottomSectionState,
  ThemeImageBannerRef,
} from "@/models/ITrackingPageOptions"
import stores from "@/stores"

import type { ImageGridItem } from "../ImageGrid/types"
import { mergeThemeBottomSection } from "./themeBottomSectionDefaults"

export interface ThemeLayoutBackdropContentDraft {
  mobile_layout: "STACKED" | "HIDE"
  bottom_section: ThemeBottomSectionState
  image_banner: ThemeImageBannerRef | undefined
}

export interface ThemeLayoutBackdropStyleDraft {
  corner_radius: "ROUNDED" | "SHARP" | "STANDARD"
  desktop_bg_color: string
  position: "CENTER" | "LEFT" | "RIGHT"
  size: "LARGE" | "SMALL" | "STANDARD"
}

export interface ThemeLayoutBackdropEditorDraft {
  content: ThemeLayoutBackdropContentDraft
  style: ThemeLayoutBackdropStyleDraft
}

export const defaultBackdropStyleDraft = (): ThemeLayoutBackdropStyleDraft => ({
  corner_radius: "STANDARD",
  desktop_bg_color: "FFFFFF",
  position: "LEFT",
  size: "STANDARD",
})

export const createInitialBackdropEditorDraft = (): ThemeLayoutBackdropEditorDraft => {
  const { displayOption } = stores.trackingPageStore

  return {
    content: {
      mobile_layout: "STACKED",
      bottom_section: mergeThemeBottomSection(displayOption?.themeBottomSection),
      image_banner: displayOption?.themeImageBanner,
    },
    style: defaultBackdropStyleDraft(),
  }
}

/** 底部区启用图 → ImageGrid 条目（最多 3） */
export const mapBottomImagesToImageGridItems = (
  images: ThemeBottomSectionImageItem[],
): ImageGridItem[] =>
  images
    .filter((i) => i.enabled !== false)
    .slice(0, 3)
    .map((i) => ({
      alt: i.image?.alt ?? "",
      src: i.image?.url?.trim() ?? "",
      destinationUrl: i.destinationUrl ?? "",
      hasImage: Boolean(i.image?.shopifyFileId?.trim()),
    }))

/** 提取十六进制数字（不含 #），最多 6 位 */
const extractHexDigits = (hexRaw: string): string =>
  hexRaw.replace(/#/g, "").replace(/[^0-9A-Fa-f]/g, "").slice(0, 6)

/**
 * 将草稿中的桌面背景色规范为 6 位大写无 # 字符串。
 * 支持 CSS 三位缩写（如 FFF → FFFFFF）；不足 3 或 4–5 位未完成输入返回 undefined。
 */
export const normalizeBackdropDesktopHexToSix = (hexRaw: string): string | undefined => {
  const hex = extractHexDigits(hexRaw)

  if (hex.length === 6) {
    return hex.toUpperCase()
  }

  if (hex.length === 3) {
    return hex
      .split("")
      .map((digit) => {
        const d = digit.toLowerCase()
        return `${d}${d}`
      })
      .join("")
      .toUpperCase()
  }

  return undefined
}

export const backdropDesktopBackgroundCss = (hexRaw: string): string => {
  const six = normalizeBackdropDesktopHexToSix(hexRaw)

  if (!six) {
    return "#cccccc"
  }

  return `#${six}`
}
